// Sesión de usuario sobre TanStack Query. La cache de la query es la ÚNICA
// fuente de verdad de la sesión: no hay Context ni estado duplicado. Cualquier
// componente que llame `useAuth()` comparte el mismo resultado y una sola
// petición GET /api/user (deduplicada por la key).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchCurrentUser,
  forgotPasswordRequest,
  loginRequest,
  logoutRequest,
  resetPasswordRequest,
  type User,
} from "@/lib/auth";

export const AUTH_USER_KEY = ["auth", "user"] as const;

export function useAuth() {
  const query = useQuery({
    queryKey: AUTH_USER_KEY,
    queryFn: fetchCurrentUser,
    retry: false, // un 401 ya se traduce a `null`; reintentar no aporta.
    staleTime: 5 * 60 * 1000,
  });

  return {
    user: query.data ?? null,
    isAuthenticated: !!query.data,
    isLoading: query.isPending,
  };
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: loginRequest,
    onSuccess: ({ user }) => {
      // Sembramos la cache con el user devuelto por el login: evita un
      // GET /api/user extra justo después de autenticar.
      queryClient.setQueryData<User>(AUTH_USER_KEY, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logoutRequest,
    onSuccess: () => {
      // Purga TODA la cache (datos de la sesión anterior) y deja la sesión en
      // `null` explícito para que el guard redirija sin un refetch intermedio.
      queryClient.clear();
      queryClient.setQueryData(AUTH_USER_KEY, null);
    },
  });
}

// Las dos mitades del flujo de recuperación son rutas PÚBLICAS (sin sesión):
// no tocan la cache de `useAuth`.

/** Paso 1: pedir el enlace por correo. */
export function useForgotPassword() {
  return useMutation({
    mutationFn: forgotPasswordRequest,
  });
}

/** Paso 2: definir la contraseña nueva con el token del enlace. */
export function useResetPassword() {
  return useMutation({
    mutationFn: resetPasswordRequest,
  });
}
